import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  Menu, 
  X, 
  Package, 
  BookOpen, 
  User,
  Command,
  BarChart3,
  GraduationCap,
  Bug,
  FileText,
  Plus,
  BookMarked,
  Settings,
  FolderTree,
  PlusCircle,
  LayoutDashboard,
  Layers,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import SearchModal from './SearchModal';
import CreateContentModal from './CreateContentModal';
import LanguageSwitcher from './LanguageSwitcher';
import EnvironmentDebug from './EnvironmentDebug';
import { usePlatformTranslation } from '../hooks/usePlatformTranslation';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../utils/cn';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isDebugOpen, setIsDebugOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const location = useLocation();
  const { t, isTranslating, translationError } = usePlatformTranslation();
  const { user, logout } = useAuth();

  // Check if we should restore search modal on back navigation
  useEffect(() => {
    const handlePopState = () => {
      const fromNavigation = sessionStorage.getItem('searchModalFromNavigation');
      if (fromNavigation === 'true') {
        setIsSearchOpen(true);
        sessionStorage.removeItem('searchModalFromNavigation');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Keyboard shortcut for search and sidebar
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        setIsSidebarCollapsed(!isSidebarCollapsed);
      }
      if (e.key === 'Escape') {
        setIsSearchOpen(false);
        setIsSidebarOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSidebarCollapsed]);

  // Close sidebar on route change
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location]);

  // Open create modal when navigating to /create or when custom event is triggered
  useEffect(() => {
    if (location.pathname === '/create') {
      setIsCreateModalOpen(true);
    }
  }, [location]);

  // Listen for custom event to open modal
  useEffect(() => {
    const handleOpenModal = () => {
      setIsCreateModalOpen(true);
    };
    
    window.addEventListener('openCreateContentModal', handleOpenModal);
    return () => {
      window.removeEventListener('openCreateContentModal', handleOpenModal);
    };
  }, []);

  const isAdmin = user?.roles?.includes('admin') || user?.roles?.includes('manager');
  const isInstructor = user?.roles?.includes('instructor');

  // Define navigation with sections
  const navigationSections = [
    {
      title: 'Overview',
      items: [
        { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      ]
    },
    {
      title: 'Learning',
      items: [
        { name: 'Course Catalog', href: '/catalog', icon: GraduationCap },
        { name: 'Collections', href: '/collections', icon: Layers },
        ...(isAdmin || isInstructor ? [{ name: t('nav.tutorials'), href: '/tutorials', icon: BookOpen }] : []),
        { name: 'Glossary', href: '/glossary', icon: BookMarked },
      ]
    },
    {
      title: 'Content',
      items: [
        ...(isAdmin || isInstructor ? [{ name: t('nav.products'), href: '/products', icon: Package }] : []),
        ...(isAdmin || isInstructor ? [{ name: 'Create Content', href: '#', icon: PlusCircle, onClick: () => setIsCreateModalOpen(true) }] : []),
        { name: 'Drafts', href: '/drafts', icon: FileText },
        { name: 'Manage Content', href: '/admin/products', icon: FolderTree },
      ]
    },
    {
      title: 'Administration',
      items: [
        { name: t('nav.admin'), href: '/admin', icon: Settings },
      ]
    }
  ];

  // Flatten for backward compatibility
  const navigation = navigationSections.flatMap(section => section.items);

  const isActive = (href: string) => {
    const hrefPath = href.split('?')[0];
    return location.pathname.startsWith(hrefPath);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo and mobile menu button */}
            <div className="flex items-center space-x-4">
              {user && (
                <button
                  onClick={() => setIsSidebarOpen(true)}
                  className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Menu className="w-5 h-5" />
                </button>
              )}
              
              <Link to="/tutorials" className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-[#667eea] to-[#764ba2] rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">V</span>
                </div>
                <span className="text-xl font-bold text-gray-900">viaPhoton</span>
              </Link>
            </div>

            {/* Search bar (authenticated only) */}
            {user && (
            <div className="flex-1 max-w-2xl mx-8 hidden md:block">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <button
                  onClick={() => setIsSearchOpen(true)}
                  className="w-full pl-10 pr-4 py-2 text-left text-gray-500 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-[#667eea] focus:border-transparent transition-colors"
                >
                  {t('common.search')} products, tutorials, and documentation...
                </button>
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1 text-xs text-gray-400">
                  <Command className="w-3 h-3" />
                  <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">K</kbd>
                </div>
              </div>
            </div>
            )}

            {/* Mobile search button (authenticated only) */}
            {user && (
              <button
                onClick={() => setIsSearchOpen(true)}
                className="md:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Search className="w-5 h-5" />
              </button>
            )}

            {/* User menu */}
            <div className="flex items-center space-x-4">
              <LanguageSwitcher />
              
              {/* Debug button */}
              <button
                onClick={() => setIsDebugOpen(true)}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Environment Debug"
              >
                <Bug className="w-4 h-4" />
              </button>
              
              {/* Debug indicator */}
              {isTranslating && (
                <div className="text-xs text-blue-600 animate-pulse">
                  Translating...
                </div>
              )}
              {translationError && (
                <div className="text-xs text-red-600">
                  Translation Error
                </div>
              )}
              
              {/* User info and logout */}
              {user ? (
                <div className="flex items-center space-x-3">
                  <div className="text-sm">
                    <div className="font-medium text-gray-900">{user.name}</div>
                    <div className="text-xs text-gray-500">
                      {user.roles?.join(', ')}
                    </div>
                  </div>
                  <button
                    onClick={logout}
                    className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <div className="text-sm text-gray-500">Not logged in</div>
                  <a
                    href="/login"
                    className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                  >
                    Login
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Desktop Sidebar */}
        {user && (
        <div className={cn(
          "hidden lg:block bg-white border-r border-gray-200 min-h-screen transition-all duration-300",
          isSidebarCollapsed ? "w-16" : "w-64"
        )}>
          <nav className="p-6" data-tour="navigation">
            {/* Sidebar Toggle Button */}
            <div className="flex items-center justify-between mb-6">
              {!isSidebarCollapsed && (
                <h2 className="text-lg font-semibold text-gray-900">Navigation</h2>
              )}
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {isSidebarCollapsed ? (
                  <Menu className="w-5 h-5 text-gray-600" />
                ) : (
                  <X className="w-5 h-5 text-gray-600" />
                )}
              </button>
            </div>

            <div className="space-y-6">
              {navigationSections.map((section, sectionIndex) => {
                // Filter out empty sections
                const sectionItems = section.items.filter(Boolean);
                if (sectionItems.length === 0) return null;

                return (
                  <div key={section.title} className={cn(sectionIndex > 0 && "pt-6 border-t border-gray-200")}>
                    {!isSidebarCollapsed && (
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-4">
                        {section.title}
                      </h3>
                    )}
                    <div className="space-y-1">
                      {sectionItems.map((item) => {
                        const Icon = item.icon;
                        const isCreateContent = item.name === 'Create Content' && item.onClick;
                        
                        if (isCreateContent) {
                          return (
                            <button
                              key={item.name}
                              onClick={item.onClick}
                              className={cn(
                                "w-full flex items-center rounded-lg text-left transition-colors",
                                "text-gray-700 hover:bg-gray-50",
                                isSidebarCollapsed ? "px-3 py-3 justify-center" : "space-x-3 px-4 py-3"
                              )}
                              title={isSidebarCollapsed ? item.name : undefined}
                            >
                              <Icon className="w-5 h-5 flex-shrink-0" />
                              {!isSidebarCollapsed && (
                                <span className="font-medium">{item.name}</span>
                              )}
                            </button>
                          );
                        }
                        
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={cn(
                              "flex items-center rounded-lg text-left transition-colors",
                              isActive(item.href)
                                ? "bg-blue-50 text-[#667eea] border border-blue-200"
                                : "text-gray-700 hover:bg-gray-50",
                              isSidebarCollapsed ? "px-3 py-3 justify-center" : "space-x-3 px-4 py-3"
                            )}
                            title={isSidebarCollapsed ? item.name : undefined}
                          >
                            <Icon className="w-5 h-5 flex-shrink-0" />
                            {!isSidebarCollapsed && (
                              <span className="font-medium">{item.name}</span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </nav>
        </div>
        )}

        {/* Mobile Sidebar Overlay */}
        {user && isSidebarOpen && (
          <motion.div
            className="fixed inset-0 z-50 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setIsSidebarOpen(false)} />
            <motion.div
              className="relative bg-white w-64 h-full shadow-xl"
              initial={{ x: -256 }}
              animate={{ x: 0 }}
              exit={{ x: -256 }}
              transition={{ duration: 0.3 }}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-[#667eea] to-[#764ba2] rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-sm">V</span>
                    </div>
                    <span className="text-xl font-bold text-gray-900">viaPhoton</span>
                  </div>
                  <button
                    onClick={() => setIsSidebarOpen(false)}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <nav className="space-y-6">
                  {navigationSections.map((section, sectionIndex) => {
                    // Filter out empty sections
                    const sectionItems = section.items.filter(Boolean);
                    if (sectionItems.length === 0) return null;

                    return (
                      <div key={section.title} className={cn(sectionIndex > 0 && "pt-6 border-t border-gray-200")}>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-4">
                          {section.title}
                        </h3>
                        <div className="space-y-1">
                          {sectionItems.map((item) => {
                            const Icon = item.icon;
                            const isCreateContent = item.name === 'Create Content' && item.onClick;
                            
                            if (isCreateContent) {
                              return (
                                <button
                                  key={item.name}
                                  onClick={() => {
                                    item.onClick?.();
                                    setIsSidebarOpen(false);
                                  }}
                                  className={cn(
                                    "w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors",
                                    "text-gray-700 hover:bg-gray-50"
                                  )}
                                >
                                  <Icon className="w-5 h-5 flex-shrink-0" />
                                  <span className="font-medium">{item.name}</span>
                                </button>
                              );
                            }
                            
                            return (
                              <Link
                                key={item.name}
                                to={item.href}
                                onClick={() => setIsSidebarOpen(false)}
                                className={cn(
                                  "flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors",
                                  isActive(item.href)
                                    ? "bg-blue-50 text-[#667eea] border border-blue-200"
                                    : "text-gray-700 hover:bg-gray-50"
                                )}
                              >
                                <Icon className="w-5 h-5 flex-shrink-0" />
                                <span className="font-medium">{item.name}</span>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </nav>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Main Content */}
        <main className="flex-1 min-h-screen transition-all duration-300">
          {children}
        </main>
      </div>

      {/* Search Modal */}
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
      
      {/* Create Content Modal */}
      <CreateContentModal 
        isOpen={isCreateModalOpen} 
        onClose={() => {
          setIsCreateModalOpen(false);
          // Navigate away from /create if we're on that route
          if (location.pathname === '/create') {
            window.history.back();
          }
        }} 
      />
      
      {/* Environment Debug Modal */}
      <EnvironmentDebug isOpen={isDebugOpen} onClose={() => setIsDebugOpen(false)} />
    </div>
  );
};

export default Layout;
